import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Wrench, Ban, Filter, Car, Clock, AlertTriangle, Search, X, ChevronDown, ChevronRight, RotateCcw, Download, FileSpreadsheet, FileText, Pencil, Trash2 } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";

const categorieLabels = {
  mecanique: { label: "Mecanique", color: "bg-blue-100 text-blue-800 border-blue-300" },
  carrosserie: { label: "Carrosserie", color: "bg-orange-100 text-orange-800 border-orange-300" },
  electricite: { label: "Electricite", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  pneus: { label: "Pneus", color: "bg-slate-100 text-slate-700 border-slate-300" },
  entretien: { label: "Entretien / Vidange", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  assurance: { label: "Assurance", color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  visite_technique: { label: "Controle technique", color: "bg-pink-100 text-pink-800 border-pink-300" },
  credit_gps: { label: "Credit GPS", color: "bg-teal-100 text-teal-800 border-teal-300" },
  frais_gestion: { label: "Frais de gestion", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  autre: { label: "Autre", color: "bg-violet-100 text-violet-800 border-violet-300" },
};

const CATEGORIES_AVEC_EXPIRATION = ["assurance", "visite_technique", "entretien", "credit_gps"];

const calcDefaultExpiration = (categorie, dateReparation) => {
  if (!dateReparation || !CATEGORIES_AVEC_EXPIRATION.includes(categorie)) return "";
  const d = new Date(dateReparation + "T12:00:00");
  if (isNaN(d.getTime())) return "";
  switch (categorie) {
    case "assurance": d.setDate(d.getDate() + 30); break;
    case "entretien": d.setDate(d.getDate() + 50); break;
    case "visite_technique": d.setFullYear(d.getFullYear() + 1); break;
    case "credit_gps": d.setMonth(d.getMonth() + 2); break;
    default: return "";
  }
  return d.toISOString().split("T")[0];
};

const raisonLabels = {
  reparation: "Reparation",
  fourriere: "Fourriere",
  absence_chauffeur: "Absence chauffeur",
  autre: "Autre",
};

function daysBetween(dateStr) {
  if (!dateStr) return 0;
  const start = new Date(dateStr + "T12:00:00");
  const now = new Date();
  return Math.max(0, Math.ceil((now - start) / (1000 * 60 * 60 * 24)));
}

export default function RepairsPage() {
  const { user } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [immobilizations, setImmobilizations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [piecesCatalog, setPiecesCatalog] = useState([]);
  const [showRepair, setShowRepair] = useState(false);
  const [continueModeRepair, setContinueModeRepair] = useState(false);
  const [editRepair, setEditRepair] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const isCurrentWeek = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    const d = new Date(dateStr + "T12:00:00");
    return d >= monday && d <= sunday;
  };
  const canEdit = user?.role === "admin" || user?.role === "gestionnaire";
  const isAdmin = user?.role === "admin";
  const [showImmo, setShowImmo] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [filterStatut, setFilterStatut] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterDateDe, setFilterDateDe] = useState("");
  const [filterDateA, setFilterDateA] = useState("");
  const [repairForm, setRepairForm] = useState({ vehicule_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "", immobilise: false, date_reparation: new Date().toISOString().split("T")[0], categorie: "mecanique", date_expiration: "" });
  const [immoForm, setImmoForm] = useState({ vehicule_id: "", raison: "reparation", description: "", date_debut: new Date().toISOString().split("T")[0] });
  const [loading, setLoading] = useState(true);
  const [pieceSearch, setPieceSearch] = useState("");
  const [showPieceDropdown, setShowPieceDropdown] = useState(false);
  const [showNewPiece, setShowNewPiece] = useState(false);
  const [newPieceForm, setNewPieceForm] = useState({ nom: "", prix: "" });

  const fetchData = async () => {
    try {
      const [rRes, iRes, vRes, pcRes] = await Promise.all([
        api.get("/repairs"), api.get("/immobilizations"), api.get("/vehicles"), api.get("/pieces-catalog")
      ]);
      setRepairs(rRes.data); setImmobilizations(iRes.data); setVehicles(vRes.data); setPiecesCatalog(pcRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getVehicleName = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? `${v.marque} ${v.modele}` : "-"; };
  const getVehicleImmat = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? v.immatriculation : ""; };
  const getVehicleStatus = (id) => { const v = vehicles.find(vh => vh.id === id); return v?.statut || "actif"; };

  const handleRepairSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editRepair) {
        await api.put(`/repairs/${editRepair.id}`, { ...repairForm, main_oeuvre: parseFloat(repairForm.main_oeuvre || 0), frais_annexes: parseFloat(repairForm.frais_annexes || 0) });
        toast.success("Reparation modifiee avec succes");
        setEditRepair(null);
        setShowRepair(false);
        setRepairForm({ vehicule_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "", immobilise: false, date_reparation: new Date().toISOString().split("T")[0], categorie: "mecanique", date_expiration: "" });
        setPieceSearch(""); setShowPieceDropdown(false);
      } else {
        // Check duplicate before creating
        const dupCheck = await api.get(`/repairs/check-duplicate?vehicule_id=${repairForm.vehicule_id}&categorie=${repairForm.categorie}&date_reparation=${repairForm.date_reparation}`);
        if (dupCheck.data.exists) {
          const dup = dupCheck.data.repair;
          const vNom = dupCheck.data.vehicule_nom;
          const confirmed = window.confirm(
            `Une reparation similaire existe deja :\n\n` +
            `Vehicule : ${vNom}\n` +
            `Categorie : ${(categorieLabels[dup.categorie] || {}).label || dup.categorie}\n` +
            `Date : ${dup.date_reparation}\n` +
            `Description : ${dup.description}\n` +
            `Total : ${dup.total} Frc\n\n` +
            `Voulez-vous quand meme enregistrer cette nouvelle reparation ?`
          );
          if (!confirmed) return;
        }
        await api.post("/repairs", { ...repairForm, main_oeuvre: parseFloat(repairForm.main_oeuvre || 0), frais_annexes: parseFloat(repairForm.frais_annexes || 0) });
        toast.success("Reparation enregistree avec succes");
        fetchData();
        if (continueModeRepair) {
          setRepairForm({ vehicule_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "", immobilise: false, date_reparation: new Date().toISOString().split("T")[0], categorie: "mecanique", date_expiration: "" });
          setPieceSearch(""); setShowPieceDropdown(false);
        } else {
          setShowRepair(false);
          setRepairForm({ vehicule_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "", immobilise: false, date_reparation: new Date().toISOString().split("T")[0], categorie: "mecanique", date_expiration: "" });
          setPieceSearch(""); setShowPieceDropdown(false);
        }
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEditRepair = (r) => {
    setEditRepair(r);
    setRepairForm({
      vehicule_id: r.vehicule_id || "",
      description: r.description || "",
      pieces: r.pieces || [],
      main_oeuvre: r.main_oeuvre || "",
      frais_annexes: r.frais_annexes || "",
      immobilise: r.immobilise || false,
      date_reparation: r.date_reparation || new Date().toISOString().split("T")[0],
      categorie: r.categorie || "mecanique",
      date_expiration: "",
    });
    setShowRepair(true);
  };

  const handleImmoSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/immobilizations", immoForm);
      toast.success("Immobilisation enregistree");
      setShowImmo(false);
      setImmoForm({ vehicule_id: "", raison: "reparation", description: "", date_debut: new Date().toISOString().split("T")[0] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleEndImmo = async (id) => {
    try {
      await api.put(`/immobilizations/${id}`, { actif: false });
      toast.success("Immobilisation terminee");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleEndRepair = async (id) => {
    try {
      await api.put(`/repairs/${id}`, { statut: "termine" });
      toast.success("Reparation terminee");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDeleteRepair = async (id) => {
    if (!window.confirm("Supprimer cette reparation ?")) return;
    try {
      await api.delete(`/repairs/${id}`);
      toast.success("Reparation supprimee");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };


  const addPieceFromCatalog = (piece) => {
    const already = repairForm.pieces.find(p => p.nom.toLowerCase() === piece.nom.toLowerCase());
    if (already) { toast.info("Piece deja ajoutee"); return; }
    setRepairForm({ ...repairForm, pieces: [...repairForm.pieces, { nom: piece.nom, prix: piece.prix_defaut || 0, quantite: 1 }] });
    setPieceSearch(""); setShowPieceDropdown(false);
  };
  const addCustomPiece = async () => {
    const nom = newPieceForm.nom.trim();
    if (!nom) return;
    const prix = parseFloat(newPieceForm.prix || 0);
    try {
      const res = await api.post("/pieces-catalog", { nom, prix });
      setPiecesCatalog(prev => {
        const exists = prev.find(p => p.nom_lower === nom.toLowerCase());
        return exists ? prev : [...prev, res.data].sort((a, b) => a.nom.localeCompare(b.nom));
      });
      setRepairForm({ ...repairForm, pieces: [...repairForm.pieces, { nom, prix, quantite: 1 }] });
      setNewPieceForm({ nom: "", prix: "" }); setShowNewPiece(false);
      toast.success(`"${nom}" ajoutee au catalogue`);
    } catch (e) { toast.error("Erreur"); }
  };
  const updatePiece = (i, field, value) => { const p = [...repairForm.pieces]; p[i][field] = (field === "prix" || field === "quantite") ? parseFloat(value || 0) : value; setRepairForm({ ...repairForm, pieces: p }); };
  const removePiece = (i) => setRepairForm({ ...repairForm, pieces: repairForm.pieces.filter((_, idx) => idx !== i) });

  const filteredCatalog = piecesCatalog.filter(pc =>
    pc.nom.toLowerCase().includes(pieceSearch.toLowerCase()) &&
    !repairForm.pieces.find(p => p.nom.toLowerCase() === pc.nom.toLowerCase())
  );

  const piecesTotal = repairForm.pieces.reduce((s, p) => s + (p.prix || 0), 0);
  const formTotal = piecesTotal + parseFloat(repairForm.main_oeuvre || 0) + parseFloat(repairForm.frais_annexes || 0);

  // Filtered
  const filteredRepairs = repairs.filter(r => {
    if (selectedVehicles.length > 0 && !selectedVehicles.includes(r.vehicule_id)) return false;
    if (filterStatut !== "all" && r.statut !== filterStatut) return false;
    if (selectedCategories.length > 0 && !selectedCategories.includes(r.categorie)) return false;
    if (filterDateDe && r.date_reparation && r.date_reparation < filterDateDe) return false;
    if (filterDateA && r.date_reparation && r.date_reparation > filterDateA) return false;
    return true;
  });
  const filteredImmos = immobilizations.filter(i => {
    if (selectedVehicles.length > 0 && !selectedVehicles.includes(i.vehicule_id)) return false;
    if (filterStatut === "en_cours" && !i.actif) return false;
    if (filterStatut === "termine" && i.actif) return false;
    return true;
  });

  // Stats
  const activeRepairs = repairs.filter(r => r.statut === "en_cours").length;
  const totalCost = repairs.reduce((s, r) => s + (r.total || 0), 0);
  const activeImmos = immobilizations.filter(i => i.actif).length;
  const immobilizedVehicles = [...new Set(immobilizations.filter(i => i.actif).map(i => i.vehicule_id))].length;

  // Filtered total cost
  const filteredTotalCost = filteredRepairs.reduce((s, r) => s + (r.total || 0), 0);

  const hasActiveFilters = selectedVehicles.length > 0 || filterStatut !== "all" || selectedCategories.length > 0 || filterDateDe || filterDateA;

  const toggleVehicle = (vehicleId) => {
    setSelectedVehicles(prev =>
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };

  const toggleCategorie = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const getVehicleImmatById = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? v.immatriculation : ""; };

  const vehicleFilterLabel = selectedVehicles.length === 0
    ? "Tous les vehicules"
    : selectedVehicles.length === 1
      ? `${getVehicleImmatById(selectedVehicles[0])} - ${getVehicleName(selectedVehicles[0])}`
      : `${selectedVehicles.length} vehicule(s)`;

  const repairExportCols = [
    { label: "Vehicule", accessor: r => `${getVehicleName(r.vehicule_id)} (${getVehicleImmat(r.vehicule_id)})` },
    { label: "Categorie", key: "categorie" },
    { label: "Description", key: "description" },
    { label: "Date", key: "date_reparation" },
    { label: "Pieces", accessor: r => (r.pieces || []).map(p => `${p.nom} (${p.prix} Frc)`).join(", ") },
    { label: "Cout total (Frc)", key: "total" },
    { label: "Statut", key: "statut" },
  ];

  const immoExportCols = [
    { label: "Vehicule", accessor: i => `${getVehicleName(i.vehicule_id)} (${getVehicleImmat(i.vehicule_id)})` },
    { label: "Raison", key: "raison" },
    { label: "Description", key: "description" },
    { label: "Debut", key: "date_debut" },
    { label: "Fin", key: "date_fin" },
    { label: "Actif", accessor: i => i.actif ? "Oui" : "Non" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="repairs-page">
      <Tabs defaultValue="repairs">
        <div className="page-header">
          <div>
            <h1 className="page-title">Reparations & Immobilisations</h1>
            <p className="text-sm text-slate-500 mt-1">{activeRepairs} reparation(s) en cours, {immobilizedVehicles} vehicule(s) immobilise(s)</p>
          </div>
          <TabsList>
            <TabsTrigger value="repairs" data-testid="tab-repairs"><Wrench className="w-4 h-4 mr-1" />Reparations</TabsTrigger>
            <TabsTrigger value="immo" data-testid="tab-immo"><Ban className="w-4 h-4 mr-1" />Immobilisations</TabsTrigger>
          </TabsList>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="repair-stats">
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Wrench className="w-4 h-4 text-blue-600" /></div></div>
            <p className="text-xl font-bold text-blue-600 font-heading">{activeRepairs}</p>
            <p className="text-xs text-slate-500">Reparations en cours</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center"><Ban className="w-4 h-4 text-red-600" /></div></div>
            <p className="text-xl font-bold text-red-600 font-heading">{immobilizedVehicles}</p>
            <p className="text-xs text-slate-500">Vehicule(s) immobilise(s)</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Car className="w-4 h-4 text-amber-600" /></div></div>
            <p className="text-xl font-bold text-amber-600 font-heading">{totalCost.toLocaleString("fr-FR")} Frc</p>
            <p className="text-xs text-slate-500">Cout total reparations</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-emerald-600" /></div></div>
            <p className="text-xl font-bold text-emerald-600 font-heading">{repairs.filter(r => r.statut === "termine").length}</p>
            <p className="text-xs text-slate-500">Reparations terminees</p>
          </div>
          <div className="stat-card border-2 border-[#0F52BA]/20">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center"><Filter className="w-4 h-4 text-[#0F52BA]" /></div></div>
            <p className="text-xl font-bold text-[#0F52BA] font-heading" data-testid="filtered-total-cost">{filteredTotalCost.toLocaleString("fr-FR")} Frc</p>
            <p className="text-xs text-slate-500">Total cout (filtre)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 sm:gap-3 mb-6 flex-wrap">
          {/* Vehicle multi-select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-[250px] justify-between text-sm" data-testid="filter-vehicles">
                <div className="flex items-center gap-2 truncate">
                  <Car className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate">{vehicleFilterLabel}</span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <div className="p-3 border-b">
                <button
                  type="button"
                  className="flex items-center gap-2 w-full text-sm font-medium text-[#0F52BA] hover:underline"
                  onClick={() => setSelectedVehicles([])}
                  data-testid="btn-select-all-vehicles"
                >
                  Tous les vehicules
                </button>
              </div>
              <div className="max-h-[240px] overflow-y-auto p-2 space-y-1">
                {vehicles.map(v => (
                  <label
                    key={v.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm"
                    data-testid={`vehicle-option-${v.id}`}
                  >
                    <Checkbox
                      checked={selectedVehicles.includes(v.id)}
                      onCheckedChange={() => toggleVehicle(v.id)}
                    />
                    <span className="truncate">{v.immatriculation} - {v.marque} {v.modele}</span>
                    {v.statut === "immobilise" && <Badge variant="outline" className="text-[10px] ml-auto bg-violet-50 text-violet-700 border-violet-200">IMMOB.</Badge>}
                  </label>
                ))}
              </div>
              {selectedVehicles.length > 0 && (
                <div className="p-2 border-t">
                  <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setSelectedVehicles([])} data-testid="btn-clear-vehicle-filter">
                    <X className="w-3 h-3 mr-1" />Effacer la selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-repair-statut"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="termine">Termine</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-[220px] justify-between text-sm" data-testid="filter-categorie">
                <div className="flex items-center gap-2 truncate">
                  <Wrench className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate">
                    {selectedCategories.length === 0
                      ? "Toutes categories"
                      : selectedCategories.length === 1
                        ? (categorieLabels[selectedCategories[0]]?.label || selectedCategories[0])
                        : `${selectedCategories.length} categorie(s)`}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <div className="p-3 border-b">
                <button
                  type="button"
                  className="flex items-center gap-2 w-full text-sm font-medium text-[#0F52BA] hover:underline"
                  onClick={() => setSelectedCategories([])}
                  data-testid="btn-select-all-categories"
                >
                  Toutes les categories
                </button>
              </div>
              <div className="max-h-[280px] overflow-y-auto p-2 space-y-1">
                {Object.entries(categorieLabels).map(([k, v]) => (
                  <label
                    key={k}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm"
                    data-testid={`categorie-option-${k}`}
                  >
                    <Checkbox
                      checked={selectedCategories.includes(k)}
                      onCheckedChange={() => toggleCategorie(k)}
                    />
                    <Badge variant="outline" className={`${v.color} text-xs`}>{v.label}</Badge>
                  </label>
                ))}
              </div>
              {selectedCategories.length > 0 && (
                <div className="p-2 border-t">
                  <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setSelectedCategories([])} data-testid="btn-clear-categorie-filter">
                    <X className="w-3 h-3 mr-1" />Effacer la selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
              <Input type="date" value={filterDateDe} onChange={e => setFilterDateDe(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-de" />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
              <Input type="date" value={filterDateA} onChange={e => setFilterDateA(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-a" />
            </div>
          </div>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={() => { setSelectedVehicles([]); setFilterStatut("all"); setSelectedCategories([]); setFilterDateDe(""); setFilterDateA(""); }} data-testid="btn-clear-filters">
              <RotateCcw className="w-4 h-4 mr-1" />Reinitialiser
            </Button>
          )}
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="btn-export-repairs"><Download className="w-4 h-4 mr-2" />Exporter</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCSV(filteredRepairs, repairExportCols, "reparations")} data-testid="export-repairs-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportExcel(filteredRepairs, repairExportCols, "reparations")} data-testid="export-repairs-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportPDF(filteredRepairs, repairExportCols, "reparations", "Reparations")} data-testid="export-repairs-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <TabsContent value="repairs">
          <div className="flex justify-end mb-4">
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setEditRepair(null); setRepairForm({ vehicule_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "", immobilise: false, date_reparation: new Date().toISOString().split("T")[0], categorie: "mecanique", date_expiration: "" }); setShowRepair(true); }} data-testid="btn-add-repair"><Plus className="w-4 h-4 mr-2" />Reparation</Button>
          </div>
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                  <TableHead className="font-semibold text-slate-600">Categorie</TableHead>
                  <TableHead className="font-semibold text-slate-600">Description</TableHead>
                  <TableHead className="font-semibold text-slate-600">Date</TableHead>
                  <TableHead className="font-semibold text-slate-600">Cout</TableHead>
                  <TableHead className="font-semibold text-slate-600">Immobilise</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRepairs.map(r => {
                  const cat = categorieLabels[r.categorie] || categorieLabels.autre;
                  const hasPieces = (r.pieces || []).length > 0;
                  const hasDetails = hasPieces || r.main_oeuvre > 0 || r.frais_annexes > 0;
                  const isExpanded = expandedRows[r.id];
                  return (
                    <>
                      <TableRow key={r.id} data-testid={`repair-row-${r.id}`} className={`${r.statut === "termine" ? "opacity-60" : ""} ${hasDetails ? "cursor-pointer hover:bg-slate-50/80" : ""}`} onClick={() => hasDetails && toggleRow(r.id)}>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {hasDetails && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                            <div>
                              <div className="font-medium text-slate-900">{getVehicleName(r.vehicule_id)}</div>
                              <div className="text-xs text-slate-500">{getVehicleImmat(r.vehicule_id)}</div>
                              {getVehicleStatus(r.vehicule_id) === "immobilise" && <Badge variant="outline" className="mt-1 text-[10px] bg-violet-50 text-violet-700 border-violet-200">Immobilise</Badge>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className={cat.color}>{cat.label}</Badge></TableCell>
                        <TableCell className="text-slate-600 max-w-[200px]">{r.description}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{r.date_reparation ? new Date(r.date_reparation + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900">{(r.total || 0).toLocaleString("fr-FR")} Frc</div>
                          {hasPieces && !isExpanded && (
                            <div className="text-[10px] text-slate-400">{(r.pieces || []).length} piece(s)</div>
                          )}
                        </TableCell>
                        <TableCell>{r.immobilise ? <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">Oui</Badge> : <span className="text-slate-400">Non</span>}</TableCell>
                        <TableCell><Badge variant="outline" className={r.statut === "en_cours" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{r.statut === "en_cours" ? "En cours" : "Termine"}</Badge></TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {r.statut === "en_cours" && <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleEndRepair(r.id)} data-testid={`btn-end-repair-${r.id}`}>Terminer</Button>}
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditRepair(r)} data-testid={`btn-edit-repair-${r.id}`}>
                              <Pencil className="w-4 h-4 text-[#0F52BA]" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteRepair(r.id)} data-testid={`btn-delete-repair-${r.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasDetails && isExpanded && (
                        <TableRow key={`${r.id}-detail`} data-testid={`repair-detail-${r.id}`}>
                          <TableCell colSpan={8} className="bg-slate-50/70 py-3 px-6">
                            {hasPieces && (
                              <>
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Detail des pieces</div>
                                <div className="space-y-1.5 mb-3">
                                  {(r.pieces || []).map((p, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5 border border-slate-100">
                                      <span className="text-slate-700 font-medium">{p.nom}</span>
                                      <div className="flex items-center gap-3">
                                        {(p.quantite || 1) > 1 && <span className="text-slate-500 text-xs">x{p.quantite}</span>}
                                        <span className="text-slate-500 text-xs">{(p.prix || 0).toLocaleString("fr-FR")} Frc/u</span>
                                        <span className="text-slate-900 font-semibold">{((p.prix || 0) * (p.quantite || 1)).toLocaleString("fr-FR")} Frc</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              {hasPieces && <span>Sous-total pieces : <strong>{(r.pieces || []).reduce((s, p) => s + (p.prix || 0) * (p.quantite || 1), 0).toLocaleString("fr-FR")} Frc</strong></span>}
                              {r.main_oeuvre > 0 && <span>Main d'oeuvre : <strong>{r.main_oeuvre} Frc</strong></span>}
                              {r.frais_annexes > 0 && <span>Frais annexes : <strong>{r.frais_annexes} Frc</strong></span>}
                              <span className="ml-auto font-semibold text-slate-900">Total : {(r.total || 0).toLocaleString("fr-FR")} Frc</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
                {filteredRepairs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Aucune reparation</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="immo">
          <div className="flex justify-end mb-4">
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => setShowImmo(true)} data-testid="btn-add-immo"><Plus className="w-4 h-4 mr-2" />Immobilisation</Button>
          </div>
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                  <TableHead className="font-semibold text-slate-600">Raison</TableHead>
                  <TableHead className="font-semibold text-slate-600">Description</TableHead>
                  <TableHead className="font-semibold text-slate-600">Debut</TableHead>
                  <TableHead className="font-semibold text-slate-600">Fin</TableHead>
                  <TableHead className="font-semibold text-slate-600">Duree</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredImmos.map(immo => {
                  const days = immo.actif ? daysBetween(immo.date_debut) : (immo.date_fin ? daysBetween(immo.date_debut) - daysBetween(immo.date_fin) : 0);
                  return (
                    <TableRow key={immo.id} data-testid={`immo-row-${immo.id}`} className={!immo.actif ? "opacity-60" : ""}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{getVehicleName(immo.vehicule_id)}</div>
                        <div className="text-xs text-slate-500">{getVehicleImmat(immo.vehicule_id)}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline">{raisonLabels[immo.raison] || immo.raison}</Badge></TableCell>
                      <TableCell className="text-slate-600">{immo.description || "-"}</TableCell>
                      <TableCell className="text-slate-600">{immo.date_debut ? new Date(immo.date_debut + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                      <TableCell className="text-slate-600">{immo.date_fin ? new Date(immo.date_fin + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span className={`font-medium text-sm ${immo.actif && days > 7 ? "text-red-600" : immo.actif ? "text-amber-600" : "text-slate-500"}`}>{days}j</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className={immo.actif ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{immo.actif ? "Active" : "Terminee"}</Badge></TableCell>
                      <TableCell className="text-right">
                        {immo.actif && <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleEndImmo(immo.id)} data-testid={`btn-end-immo-${immo.id}`}>Terminer</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredImmos.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Aucune immobilisation</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Repair Dialog */}
      <Dialog open={showRepair} onOpenChange={setShowRepair}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="repair-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editRepair ? "Modifier la reparation" : "Enregistrer une reparation"}</DialogTitle></DialogHeader>
          <form onSubmit={handleRepairSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicule</Label>
                <Select value={repairForm.vehicule_id} onValueChange={v => setRepairForm({ ...repairForm, vehicule_id: v })}>
                  <SelectTrigger data-testid="select-repair-vehicle"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={repairForm.categorie} onValueChange={v => setRepairForm({ ...repairForm, categorie: v, date_expiration: calcDefaultExpiration(v, repairForm.date_reparation) })}>
                  <SelectTrigger data-testid="select-repair-categorie"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categorieLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {CATEGORIES_AVEC_EXPIRATION.includes(repairForm.categorie) && (
              <div className="p-3 rounded-lg border-2 border-dashed border-[#0F52BA]/30 bg-blue-50/50">
                <Label className="text-[#0F52BA] font-semibold text-sm">
                  Date d'expiration ({categorieLabels[repairForm.categorie]?.label})
                  <span className="text-xs font-normal text-slate-500 ml-1">— sera mise a jour sur le vehicule</span>
                </Label>
                <Input type="date" value={repairForm.date_expiration} onChange={e => setRepairForm({ ...repairForm, date_expiration: e.target.value })} required data-testid="input-date-expiration" className="mt-1" />
              </div>
            )}
            <div>
              <Label>Date de reparation</Label>
              <Input type="date" value={repairForm.date_reparation} onChange={e => {
                const newDate = e.target.value;
                const newExp = CATEGORIES_AVEC_EXPIRATION.includes(repairForm.categorie) ? calcDefaultExpiration(repairForm.categorie, newDate) : repairForm.date_expiration;
                setRepairForm({ ...repairForm, date_reparation: newDate, date_expiration: newExp });
              }} data-testid="input-repair-date" />
            </div>
            <div><Label>Description</Label><Textarea value={repairForm.description} onChange={e => setRepairForm({ ...repairForm, description: e.target.value })} data-testid="input-repair-desc" /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Pieces ({repairForm.pieces.length})</Label>
              </div>
              {/* Pieces added */}
              {repairForm.pieces.map((p, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center bg-slate-50 rounded-lg px-3 py-2" data-testid={`piece-row-${i}`}>
                  <span className="flex-1 text-sm font-medium text-slate-800">{p.nom}</span>
                  <Input type="number" min="1" step="1" value={p.quantite || 1} onChange={e => updatePiece(i, "quantite", e.target.value)} className="w-16 text-center" placeholder="Qte" data-testid={`input-piece-qte-${i}`} />
                  <span className="text-xs text-slate-400">x</span>
                  <Input type="number" step="0.01" value={p.prix} onChange={e => updatePiece(i, "prix", e.target.value)} className="w-28" placeholder="Prix" data-testid={`input-piece-prix-${i}`} />
                  <span className="text-xs text-slate-400">Frc</span>
                  <span className="text-xs font-semibold text-slate-700 w-20 text-right">{((p.prix || 0) * (p.quantite || 1)).toLocaleString("fr-FR")}</span>
                  <Button type="button" variant="ghost" size="icon" className="shrink-0 text-red-500 hover:text-red-700 h-7 w-7" onClick={() => removePiece(i)} data-testid={`btn-remove-piece-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {/* Search / select piece */}
              <div className="relative mt-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher une piece..."
                      value={pieceSearch}
                      onChange={e => { setPieceSearch(e.target.value); setShowPieceDropdown(true); }}
                      onFocus={() => setShowPieceDropdown(true)}
                      className="pl-8"
                      data-testid="input-piece-search"
                    />
                  </div>
                  <Button type="button" variant="outline" size="sm" className="whitespace-nowrap text-[#0F52BA] border-[#0F52BA]/30 hover:bg-[#0F52BA]/5" onClick={() => { setShowNewPiece(true); setNewPieceForm({ nom: pieceSearch, prix: "" }); setShowPieceDropdown(false); }} data-testid="btn-new-piece">
                    <Plus className="w-3.5 h-3.5 mr-1" />Nouvelle
                  </Button>
                </div>
                {showPieceDropdown && pieceSearch.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="piece-dropdown">
                    {filteredCatalog.length > 0 ? filteredCatalog.map(pc => (
                      <button key={pc.id} type="button" className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between text-sm" onClick={() => addPieceFromCatalog(pc)} data-testid={`piece-option-${pc.id}`}>
                        <span className="font-medium text-slate-800">{pc.nom}</span>
                        {pc.prix_defaut > 0 && <span className="text-slate-400 text-xs">{pc.prix_defaut} Frc</span>}
                      </button>
                    )) : (
                      <div className="px-3 py-3 text-center">
                        <p className="text-sm text-slate-500">Aucune piece trouvee pour "{pieceSearch}"</p>
                        <Button type="button" variant="link" size="sm" className="text-[#0F52BA] mt-1" onClick={() => { setShowNewPiece(true); setNewPieceForm({ nom: pieceSearch, prix: "" }); setShowPieceDropdown(false); }} data-testid="btn-create-from-search">
                          Creer cette piece
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* New piece inline form */}
              {showNewPiece && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="new-piece-form">
                  <p className="text-xs font-semibold text-blue-700">Ajouter une nouvelle piece au catalogue</p>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Nom</Label>
                      <Input value={newPieceForm.nom} onChange={e => setNewPieceForm({ ...newPieceForm, nom: e.target.value })} placeholder="Nom de la piece" data-testid="input-new-piece-nom" />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Prix (Frc)</Label>
                      <Input type="number" step="0.01" value={newPieceForm.prix} onChange={e => setNewPieceForm({ ...newPieceForm, prix: e.target.value })} placeholder="0" data-testid="input-new-piece-prix" />
                    </div>
                    <Button type="button" size="sm" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={addCustomPiece} data-testid="btn-confirm-new-piece">Ajouter</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewPiece(false)}>Annuler</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Main d'oeuvre (Frc)</Label><Input type="number" value={repairForm.main_oeuvre} onChange={e => setRepairForm({ ...repairForm, main_oeuvre: e.target.value })} data-testid="input-main-oeuvre" /></div>
              <div><Label>Frais annexes (Frc)</Label><Input type="number" value={repairForm.frais_annexes} onChange={e => setRepairForm({ ...repairForm, frais_annexes: e.target.value })} data-testid="input-frais-annexes" /></div>
            </div>
            {/* Cost summary */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between text-slate-500"><span>Pieces ({repairForm.pieces.length})</span><span>{piecesTotal} Frc</span></div>
              <div className="flex justify-between text-slate-500"><span>Main d'oeuvre</span><span>{parseFloat(repairForm.main_oeuvre || 0)} Frc</span></div>
              <div className="flex justify-between text-slate-500"><span>Frais annexes</span><span>{parseFloat(repairForm.frais_annexes || 0)} Frc</span></div>
              <div className="border-t pt-1 mt-1 flex justify-between font-semibold text-slate-900"><span>Total</span><span>{formTotal} Frc</span></div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <Switch checked={repairForm.immobilise} onCheckedChange={v => setRepairForm({ ...repairForm, immobilise: v })} data-testid="switch-immobilise" />
              <div>
                <Label className="text-amber-800">Vehicule immobilise</Label>
                <p className="text-[11px] text-amber-600">Le vehicule sera marque comme indisponible dans le planning</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              {!editRepair ? (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600" data-testid="toggle-continue-repair">
                  <Checkbox checked={continueModeRepair} onCheckedChange={setContinueModeRepair} />
                  <span>Enchainer les saisies</span>
                </label>
              ) : <div />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowRepair(false); setEditRepair(null); }}>Annuler</Button>
                <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-repair">{editRepair ? "Modifier" : "Enregistrer"}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Immobilization Dialog */}
      <Dialog open={showImmo} onOpenChange={setShowImmo}>
        <DialogContent data-testid="immo-dialog">
          <DialogHeader><DialogTitle className="font-heading">Enregistrer une immobilisation</DialogTitle></DialogHeader>
          <form onSubmit={handleImmoSubmit} className="space-y-4">
            <div>
              <Label>Vehicule</Label>
              <Select value={immoForm.vehicule_id} onValueChange={v => setImmoForm({ ...immoForm, vehicule_id: v })}>
                <SelectTrigger data-testid="select-immo-vehicle"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raison</Label>
              <Select value={immoForm.raison} onValueChange={v => setImmoForm({ ...immoForm, raison: v })}>
                <SelectTrigger data-testid="select-immo-raison"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reparation">Reparation</SelectItem>
                  <SelectItem value="fourriere">Fourriere</SelectItem>
                  <SelectItem value="absence_chauffeur">Absence chauffeur</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={immoForm.description} onChange={e => setImmoForm({ ...immoForm, description: e.target.value })} data-testid="input-immo-desc" /></div>
            <div><Label>Date debut</Label><Input type="date" value={immoForm.date_debut} onChange={e => setImmoForm({ ...immoForm, date_debut: e.target.value })} required data-testid="input-immo-date" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowImmo(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-immo">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
